import React, { useState } from 'react';
import { 
  TrendingUp, 
  DollarSign,
  Users,
  Truck,
  Award,
  CheckCircle2,
  Quote,
  ArrowUpRight,
  AlertCircle
} from 'lucide-react';
import Container from '../ui/Container';
import Card from '../ui/Card';
import './Resultados.css';

const Resultados = () => {
  const [casoActivo, setCasoActivo] = useState(0);

  const casos = [
    {
      empresa: "Transportes del Norte",
      ubicacion: "Lima - Trujillo",
      vehiculos: 5,
      tiempo: "3 meses usando el sistema",
      antes: {
        rentabilidad: "No conocía rentabilidad real",
        perdidas: "S/ 3,800/mes en viajes no rentables",
        control: "Excel desactualizado",
        clientes: "Trabajaba con todos sin filtro"
      },
      despues: {
        rentabilidad: "+22% rentabilidad mensual",
        recuperado: "S/ 4,200/mes recuperados",
        control: "Control en tiempo real",
        clientes: "3 clientes no rentables identificados"
      },
      metricas: [
        { label: "Recuperado mensual", valor: "S/ 4,200", cambio: "+22%" },
        { label: "Viajes rentables", valor: "89%", cambio: "vs 64% antes" },
        { label: "Tiempo en reportes", valor: "15 min", cambio: "vs 6 horas antes" }
      ],
      testimonio: "Antes cerraba el mes y me sorprendía con pérdidas. Ahora sé al instante si un viaje me conviene o no. En 3 meses recuperé más de lo que invertí."
    },
    {
      empresa: "Carga Express SAC",
      ubicacion: "Arequipa",
      vehiculos: 8,
      tiempo: "5 meses usando el sistema",
      antes: {
        rentabilidad: "Calculaba 'a ojo'",
        perdidas: "S/ 6,500/mes sin detectar",
        control: "Papeles y facturas perdidas",
        clientes: "No sabía cuáles eran rentables"
      },
      despues: {
        rentabilidad: "+28% margen neto",
        recuperado: "S/ 7,100/mes recuperados",
        control: "Todo digitalizado y organizado",
        clientes: "Top 5 clientes identificados"
      },
      metricas: [
        { label: "Recuperado mensual", valor: "S/ 7,100", cambio: "+28%" },
        { label: "Viajes rentables", valor: "92%", cambio: "vs 58% antes" },
        { label: "Clientes activos", valor: "12", cambio: "vs 18 antes (optimizado)" }
      ],
      testimonio: "Descubrí que estaba trabajando para perder plata. Tenía clientes que me llenaban de trabajo pero me dejaban en pérdida. Ahora solo trabajo lo que me conviene."
    },
    {
      empresa: "Rutas del Sur",
      ubicacion: "Cusco - Lima",
      vehiculos: 4,
      tiempo: "2 meses usando el sistema",
      antes: {
        rentabilidad: "Sin visibilidad de costos",
        perdidas: "S/ 2,400/mes ocultas",
        control: "Control mental y cuadernos",
        clientes: "Aceptaba cualquier viaje"
      },
      despues: {
        rentabilidad: "+18% rentabilidad",
        recuperado: "S/ 2,850/mes recuperados",
        control: "Alertas automáticas funcionando",
        clientes: "2 clientes reemplazados por rentables"
      },
      metricas: [
        { label: "Recuperado mensual", valor: "S/ 2,850", cambio: "+18%" },
        { label: "Viajes rentables", valor: "85%", cambio: "vs 68% antes" },
        { label: "ROI del sistema", valor: "1er mes", cambio: "Se pagó solo" }
      ],
      testimonio: "En solo 2 meses ya vi resultados. Evité viajes que antes habría aceptado sin pensar. El sistema se pagó solo en el primer mes."
    }
  ];

  const metricas_globales = [
    {
      icon: <TrendingUp size={32} />,
      valor: "15-28%",
      label: "Mejora en rentabilidad",
      descripcion: "Promedio en los primeros 3 meses"
    },
    {
      icon: <DollarSign size={32} />,
      valor: "S/ 3,500",
      label: "Recuperado mensual",
      descripcion: "Por empresa con 5 vehículos"
    },
    {
      icon: <Truck size={32} />,
      valor: "80-90%",
      label: "Viajes rentables",
      descripcion: "vs 55-70% antes del sistema"
    },
    {
      icon: <Users size={32} />,
      valor: "30-40%",
      label: "Clientes optimizados",
      descripcion: "Identifican no rentables y los reemplazan"
    }
  ];

  const beneficios_reportados = [
    "Detectaron viajes no rentables en la primera semana",
    "Identificaron clientes que generaban volumen pero no utilidad",
    "Redujeron tiempo en reportes de 6-8 horas a 15 minutos",
    "Mejoraron negociación con tarifas basadas en costos reales",
    "Tomaron decisiones de inversión con datos concretos",
  ];

  return (
    <section className="resultados" id="resultados">
      <Container>
        {/* Header */}
        <div className="resultados-header">
          <div className="resultados-badge">
            <Award size={18} />
            <span>Casos de éxito</span>
          </div>
          
          <h2 className="resultados-title">
            Resultados reales de empresas de <br/>
            <span className="text-success">transporte como la tuya</span>
          </h2>
          
          <p className="resultados-subtitle">
            No son promesas. Son <strong>números reales</strong> de empresas que implementaron el sistema y ahora tienen control total de su rentabilidad.
          </p>
        </div>

        {/* Métricas globales */}
        <div className="metricas-globales">
          {metricas_globales.map((metrica, index) => (
            <div key={index} className="metrica-global-card">
              <div className="metrica-global-icon">
                {metrica.icon}
              </div>
              <div className="metrica-global-valor">{metrica.valor}</div>
              <div className="metrica-global-label">{metrica.label}</div>
              <div className="metrica-global-desc">{metrica.descripcion}</div>
            </div>
          ))}
        </div>

        {/* Casos de éxito - Selector */}
        <div className="casos-selector">
          <h3>Casos de éxito detallados</h3>
          <div className="selector-buttons">
            {casos.map((caso, index) => (
              <button
                key={index}
                className={`selector-button ${casoActivo === index ? 'active' : ''}`}
                onClick={() => setCasoActivo(index)}
              >
                <div className="selector-empresa">{caso.empresa}</div>
                <div className="selector-info">
                  {caso.vehiculos} vehículos • {caso.ubicacion}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Caso activo */}
        <div className="caso-detalle">
          <div className="caso-header">
            <div className="caso-info">
              <h3>{casos[casoActivo].empresa}</h3>
              <div className="caso-meta">
                <span className="meta-item">
                  <Truck size={16} />
                  {casos[casoActivo].vehiculos} vehículos
                </span>
                <span className="meta-item">
                  {casos[casoActivo].ubicacion}
                </span>
                <span className="meta-item meta-tiempo">
                  {casos[casoActivo].tiempo}
                </span>
              </div>
            </div>
          </div>

          {/* Comparación Antes/Después */}
          <div className="antes-despues">
            <div className="comparacion-lado lado-antes">
              <div className="comparacion-titulo">
                <AlertCircle size={20} />
                <h4>Antes del sistema</h4>
              </div>
              <ul>
                <li><strong>Rentabilidad:</strong> {casos[casoActivo].antes.rentabilidad}</li>
                <li><strong>Pérdidas:</strong> {casos[casoActivo].antes.perdidas}</li>
                <li><strong>Control:</strong> {casos[casoActivo].antes.control}</li>
                <li><strong>Clientes:</strong> {casos[casoActivo].antes.clientes}</li>
              </ul>
            </div>

            <div className="comparacion-divider">
              <ArrowUpRight size={32} />
            </div>

            <div className="comparacion-lado lado-despues">
              <div className="comparacion-titulo">
                <CheckCircle2 size={20} />
                <h4>Después del sistema</h4>
              </div>
              <ul>
                <li><strong>Rentabilidad:</strong> {casos[casoActivo].despues.rentabilidad}</li>
                <li><strong>Recuperado:</strong> {casos[casoActivo].despues.recuperado}</li>
                <li><strong>Control:</strong> {casos[casoActivo].despues.control}</li>
                <li><strong>Clientes:</strong> {casos[casoActivo].despues.clientes}</li>
              </ul>
            </div>
          </div>

          {/* Métricas del caso */}
          <div className="caso-metricas">
            {casos[casoActivo].metricas.map((metrica, index) => (
              <div key={index} className="caso-metrica-item">
                <div className="metrica-label-small">{metrica.label}</div>
                <div className="metrica-valor-grande">{metrica.valor}</div>
                <div className="metrica-cambio">{metrica.cambio}</div>
              </div>
            ))}
          </div>

          {/* Testimonio */}
          <div className="testimonio-box">
            <Quote className="quote-icon" size={32} />
            <p className="testimonio-texto">{casos[casoActivo].testimonio}</p>
            <div className="testimonio-autor">
              — Gerente General, {casos[casoActivo].empresa}
            </div>
          </div>
        </div>

        {/* Beneficios más reportados */}
        <div className="beneficios-reportados">
          <h3>Lo que más valoran nuestros clientes</h3>
          <div className="beneficios-grid">
            {beneficios_reportados.map((beneficio, index) => (
              <div key={index} className="beneficio-item">
                <CheckCircle2 size={20} className="check-icon" />
                <span>{beneficio}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Estadística final impactante */}
        <div className="estadistica-final">
          <div className="estadistica-numero">94%</div>
          <div className="estadistica-texto">
            <strong>de las empresas recuperan la inversión en el primer mes</strong> al evitar viajes no rentables que antes aceptaban sin análisis.
          </div>
        </div>

      </Container>
    </section>
  );
};

export default Resultados;